import type { PulseEvent, PulseTrackerConfig } from './types';

/**
 * Creates a tracker bound to the given configuration.
 * Sends events to the configured endpoint (typically a same-origin API route
 * that enriches the payload server-side with anonymous_id, tenant_id, etc.).
 */
export function createTracker(config: PulseTrackerConfig) {
    const { endpoint } = config;

    return {
        async track(event: PulseEvent, userId?: string | null): Promise<void> {
            try {
                const context = config.defaultContext || event.context
                    ? { ...config.defaultContext, ...event.context }
                    : undefined;

                const response = await fetch(endpoint, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    // Callers do not await track() before letting a navigation proceed — an ad
                    // click is an anchor with an onClick, so the request is in flight exactly as
                    // the document unloads, and a plain fetch() is cancelled at that point. That
                    // silently drops `ad_click`, which is the numerator of every cross-campaign
                    // ratio (CIN-14635), while impressions and closes — which never navigate —
                    // keep arriving, so the loss reads as "nobody clicks" rather than as a bug.
                    // Payloads are a few hundred bytes, far under keepalive's 64KB body limit.
                    keepalive: true,
                    body: JSON.stringify({
                        ...event,
                        context,
                        user_id: userId ?? config.userId ?? null,
                    }),
                });

                if (!response.ok) {
                    const text = await response.text().catch(() => '');
                    console.error(`Pulse track failed (${response.status}): ${text}`);
                }
            } catch (error) {
                console.error('Pulse track error:', error);
            }
        },
    };
}

export type PulseTracker = ReturnType<typeof createTracker>;
